import {
  colorMarcadorAAlmacenamiento,
  colorMarcadorDesdeAlmacenamiento,
  PanelPersonal,
  type Marcador,
  type TarjetaValor,
} from '../../domain/entities/PanelPersonal';
import { crearError, traducirErrorSupabase } from '../../domain/errores';
import { aNumero, aTexto } from '../mappers/conversiones';
import { supabase } from '../supabase/cliente';

const TABLA = 'user_dashboards';
const BUCKET = 'dashboard-images';

/** Tamaño máximo admitido para el plano de la instalación. */
const MAXIMO_IMAGEN_BYTES = 5 * 1024 * 1024;

function aLista(valor: unknown): Record<string, unknown>[] {
  if (Array.isArray(valor)) return valor as Record<string, unknown>[];

  if (typeof valor === 'string') {
    try {
      const parseado: unknown = JSON.parse(valor);
      return Array.isArray(parseado) ? (parseado as Record<string, unknown>[]) : [];
    } catch {
      return [];
    }
  }

  return [];
}

/** Mantiene la coordenada dentro del plano aunque venga con un valor extraño. */
function aPorcentaje(valor: unknown): number {
  const n = aNumero(valor) ?? 50;
  return Math.min(100, Math.max(0, n));
}

function aMarcador(fila: Record<string, unknown>, indice: number): Marcador {
  return {
    id: aTexto(fila.id) ?? `m_${indice}`,
    x: aPorcentaje(fila.x),
    y: aPorcentaje(fila.y),
    color: colorMarcadorDesdeAlmacenamiento(fila.color),
    sensorId: aNumero(fila.sensor_id),
    sensorNombre: aTexto(fila.sensor_nombre) ?? 'Sin sensor',
    sensorTipo: aTexto(fila.sensor_tipo) ?? '',
    sensorArea: aTexto(fila.sensor_area) ?? '',
    sensorUnidad: aTexto(fila.sensor_unidad) ?? '',
  };
}

function aTarjeta(fila: Record<string, unknown>, indice: number): TarjetaValor {
  return {
    id: aTexto(fila.id) ?? `t_${indice}`,
    etiqueta: aTexto(fila.label) ?? 'Sin título',
    descripcion: aTexto(fila.sub) ?? '',
    sensorId: aNumero(fila.sensor_id),
    unidad: aTexto(fila.unit) ?? '',
    tipo: aTexto(fila.tipo) ?? '',
    area: aTexto(fila.area) ?? '',
  };
}

/**
 * Guarda y recupera el panel personal de cada usuario.
 *
 * Las claves de los campos guardados se conservan tal como estaban (`label`,
 * `sub`, `unit`, colores en inglés) para que los paneles que los usuarios ya
 * habían configurado sigan abriéndose sin migración.
 */
export class RepositorioPanel {
  async obtener(usuarioId: string): Promise<PanelPersonal> {
    const { data, error } = await supabase
      .from(TABLA)
      .select('site_name, image_url, pins, stats')
      .eq('user_id', usuarioId)
      .maybeSingle();

    if (error) throw traducirErrorSupabase(error);
    if (!data) return PanelPersonal.vacio(usuarioId);

    const fila = data as Record<string, unknown>;

    return new PanelPersonal({
      usuarioId,
      nombreInstalacion: aTexto(fila.site_name) ?? 'Mi instalación',
      imagenUrl: aTexto(fila.image_url),
      marcadores: aLista(fila.pins).map(aMarcador),
      tarjetas: aLista(fila.stats).map(aTarjeta),
    });
  }

  async guardar(panel: PanelPersonal): Promise<void> {
    const { error } = await supabase.from(TABLA).upsert(
      {
        user_id: panel.usuarioId,
        site_name: panel.nombreInstalacion,
        image_url: panel.imagenUrl,
        pins: panel.marcadores.map((m) => ({
          id: m.id,
          x: m.x,
          y: m.y,
          color: colorMarcadorAAlmacenamiento(m.color),
          sensor_id: m.sensorId,
          sensor_nombre: m.sensorNombre,
          sensor_tipo: m.sensorTipo,
          sensor_area: m.sensorArea,
          sensor_unidad: m.sensorUnidad,
        })),
        stats: panel.tarjetas.map((t) => ({
          id: t.id,
          label: t.etiqueta,
          sub: t.descripcion,
          sensor_id: t.sensorId,
          unit: t.unidad,
          tipo: t.tipo,
          area: t.area,
        })),
      },
      { onConflict: 'user_id' },
    );

    if (error) throw traducirErrorSupabase(error);
  }

  /**
   * Sube el plano de la instalación y devuelve su dirección pública.
   *
   * Cada imagen va a una ruta con el identificador del usuario por delante,
   * que es lo que permite que las políticas del almacenamiento impidan a nadie
   * escribir en la carpeta de otro.
   */
  async subirImagen(usuarioId: string, archivo: File): Promise<string> {
    if (!archivo.type.startsWith('image/')) {
      throw crearError('DATOS_INVALIDOS', null, 'El archivo debe ser una imagen.');
    }
    if (archivo.size > MAXIMO_IMAGEN_BYTES) {
      throw crearError('DATOS_INVALIDOS', null, 'La imagen no puede superar los 5 MB.');
    }

    const extension = (archivo.type.split('/')[1] ?? 'png').split('+')[0];
    const ruta = `${usuarioId}/plano-${Date.now()}.${extension}`;

    const { error } = await supabase.storage
      .from(BUCKET)
      .upload(ruta, archivo, { contentType: archivo.type, upsert: false });

    if (error) throw traducirErrorSupabase(error);

    const { data } = supabase.storage.from(BUCKET).getPublicUrl(ruta);
    return data.publicUrl;
  }
}

export const repositorioPanel = new RepositorioPanel();
